import { useEffect, useState } from "react";
import { getLatestMetrics, getRuntimeStatus } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Metric {
  metric_name: string;
  value: number;
  recorded_at: string;
}
interface RuntimeStatus {
  activeAgents: number;
  totalAgents: number;
  runningJobs: number;
  queuedJobs: number;
  successRateToday: number;
  totalCostToday: number;
  totalTokensToday: number;
}

export function OpsMetricsPage() {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [status, setStatus] = useState<RuntimeStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void Promise.all([getLatestMetrics(token), getRuntimeStatus(token)]).then(
      ([m, s]: unknown[]) => {
        setMetrics((m as { items: Metric[] }).items ?? []);
        setStatus(s as RuntimeStatus);
        setLoading(false);
      },
    );
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <h1 className="mb-6 text-2xl font-bold text-white">Runtime Metrics</h1>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <>
            {status && (
              <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  { label: "Active Agents", value: `${status.activeAgents}/${status.totalAgents}` },
                  { label: "Running Jobs", value: status.runningJobs },
                  {
                    label: "Success Rate",
                    value: `${(status.successRateToday * 100).toFixed(1)}%`,
                  },
                  { label: "Tokens Today", value: status.totalTokensToday.toLocaleString() },
                ].map((s) => (
                  <div key={s.label} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                    <p className="text-xs text-gray-400">{s.label}</p>
                    <p className="mt-1 text-xl font-bold text-white">{s.value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <h2 className="mb-3 text-sm font-medium text-gray-300">Latest Metrics</h2>
              {metrics.length === 0 ? (
                <p className="text-sm text-gray-500">No metrics recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {metrics.map((m) => (
                    <div
                      key={m.metric_name}
                      className="flex items-center justify-between border-b border-gray-800 pb-2 text-sm"
                    >
                      <span className="font-mono text-xs text-gray-300">{m.metric_name}</span>
                      <div className="text-right">
                        <span className="text-white">{m.value}</span>
                        <span className="ml-2 text-xs text-gray-600">
                          {new Date(m.recorded_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
